import { useTaxCouponCreate } from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  TaxCouponCreateRequest,
  TaxCouponFormValues,
} from '@/core/domain/coupon/coupons/coupons.types';
import {
  Button,
  Empty,
  Form,
  Modal,
  ModalProps,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import InvoiceCFeMethodPayments from './MethodPayments';
import InvoiceCfeRecipientData from './CustomerData';
import { RecipientType } from '@/core/domain/invoice/invoice/Invoice.types';
import { useState } from 'react';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import { useHistory } from 'react-router';
import { useDefaultEmptyFieldError } from '@/hooks';
import { useCouponCompanyConfigDetail } from '@/core/domain/coupon/company/company.hooks';

interface Props extends ModalProps {
  onCancel: () => void;
}

const InvoiceCFeCreateModal: React.FC<Props> = (props) => {
  const [form] = Form.useForm<TaxCouponFormValues>();
  const formValues = Form.useWatch([], form);

  const taxtCouponCreate = useTaxCouponCreate();

  const [errorAddress, setErrorAddress] = useState<string | null>(null);

  const history = useHistory();

  const ruleRequired = useDefaultEmptyFieldError();

  const { data: couponCompanyConfig, isLoading: configIsLoading } =
    useCouponCompanyConfigDetail(formValues?.companyId);

  const hasCompany = !!formValues?.companyId;
  const hasTaxConfigActive = !!couponCompanyConfig?.defaultTaxConfig.active;

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (values) {
        let data = {} as TaxCouponCreateRequest;

        if (values.recipient.recipientType === RecipientType.customer) {
          data = {
            ...data,
            recipient: {
              clientName: values.recipient.customer || '',
              id: values.recipient.customerId || 0,
              cpfCnpj: values.recipient.cpfCnpj,
            },
          };
        }
        if (values.recipient.recipientType === RecipientType.insurance) {
          data = {
            ...data,
            recipient: {
              clientName: values.recipient.insurer || '',
              id: values.recipient.insurerId || 0,
              cpfCnpj: values.recipient.cpfCnpj,
            },
          };
        }
        if (values.recipient.recipientType === RecipientType.supplier) {
          data = {
            ...data,
            recipient: {
              clientName: values.recipient.supplier || '',
              id: values.recipient.supplierId || 0,
              cpfCnpj: values.recipient.cpfCnpj,
            },
          };
        }

        if (values.hasCustomerAddress) {
          const obj = await form.validateFields([
            ['recipient', 'deliveryAddress', 'state'],
            ['recipient', 'deliveryAddress', 'city'],
            ['recipient', 'deliveryAddress', 'district'],
          ]);

          if (
            obj.recipient?.deliveryAddress &&
            Object.values(obj.recipient?.deliveryAddress).every(
              (value) => !value,
            )
          ) {
            setErrorAddress('Informe os dados do endereço');
            throw new Error('Address inválid');
          } else if (
            obj.recipient?.deliveryAddress &&
            Object.values(obj.recipient?.deliveryAddress).some(
              (value) => !value,
            )
          ) {
            if (!obj.recipient?.deliveryAddress?.city) {
              setErrorAddress('Informe o bairro do endereço');
            } else if (!obj.recipient?.deliveryAddress?.city) {
              setErrorAddress('Informe a cidade do endereço');
            } else if (!obj.recipient?.deliveryAddress?.state) {
              setErrorAddress('Informe o estado do endereço');
            }
            throw new Error('Address inválid');
          }

          data = {
            ...data,
            recipient: {
              ...data.recipient,
              deliveryAddress: values.recipient.deliveryAddress,
            },
          };
        }

        const result = await taxtCouponCreate.mutateAsync({
          ...data,
          payments: values.payments,
          companyId: values.companyId,
          couponItems: [],
          origins: [],
        });

        history.push(
          `/app/fiscal/cfe/${result.id}/${values.companyId}/general`,
        );
      }
    } catch {}
  }

  function handleClose() {
    form.resetFields();
    if (props.onCancel) {
      props.onCancel();
    }
  }

  return (
    <Modal
      {...props}
      title="Cadastrar Cupom Fiscal"
      width={800}
      okText="Salvar"
      onOk={handleSubmit}
      confirmLoading={taxtCouponCreate.isLoading}
      destroyOnClose
      onCancel={handleClose}
    >
      <Form form={form}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <FloatingFormItem
            name="companyId"
            label="Empresa"
            rules={[ruleRequired]}
          >
            <CompaniesInfiniteSelectField />
          </FloatingFormItem>

          {!hasCompany && <Empty description="Selecione uma empresa" />}

          {!!configIsLoading && <Skeleton />}

          {!hasTaxConfigActive && !!formValues?.companyId && !configIsLoading && (
            <Empty
              description={
                <>
                  <p>
                    A empresa não está configurada para integração com CF-e SAT
                  </p>
                  <Button
                    type="primary"
                    ghost
                    onClick={() =>
                      window.open(
                        `/app/registry/companies/${formValues.companyId}/coupon`,
                        '_blank',
                      )
                    }
                  >
                    Configurar empresa
                  </Button>
                </>
              }
            />
          )}

          {!!hasTaxConfigActive && !configIsLoading && (
            <>
              <InvoiceCfeRecipientData setAddressError={setErrorAddress} />

              {!!errorAddress && (
                <div style={{ marginTop: -24 }}>
                  <Typography.Text type="danger">
                    {errorAddress}
                  </Typography.Text>
                </div>
              )}

              <InvoiceCFeMethodPayments />
            </>
          )}
        </Space>
      </Form>
    </Modal>
  );
};

export default InvoiceCFeCreateModal;
